import { Link, createRootRouteWithContext } from '@tanstack/react-router'
import { AlertTriangle, Home } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { ApolloClient } from '@apollo/client'
import type { QueryClient } from '@tanstack/react-query'
import type { DocumentRecordClient } from '@/integrations/zustand-store/document-record-client'
import type { CurrentUserWithPermissions } from '@/shared/lib/workspace-permissions'
import AppShell from '@/app/shell/app-shell'
import { appTitle } from '@/shared/config/app-config'
import { Button } from '@/shared/ui/button'

interface MyRouterContext {
  queryClient: QueryClient
  apolloClient: ApolloClient
  currentUser: CurrentUserWithPermissions | null
  records: DocumentRecordClient
}

export const Route = createRootRouteWithContext<MyRouterContext>()({
  component: () => <AppShell />,
  errorComponent: RootRouteError,
  notFoundComponent: RootNotFound,
})

function RootNotFound() {
  const { t } = useTranslation()

  return (
    <main className="min-h-[calc(100vh-4rem)] bg-radial-[at_top] from-accent/40 via-background to-background px-6 py-16">
      <div className="mx-auto flex max-w-3xl flex-col items-center justify-center gap-6 text-center">
        <div className="space-y-3">
          <p className="text-sm font-semibold uppercase tracking-[0.32em] text-primary">
            404
          </p>
          <h1 className="text-4xl font-semibold tracking-tight text-balance sm:text-5xl">
            This page is outside the {appTitle} flight path.
          </h1>
          <p className="text-base leading-7 text-muted-foreground sm:text-lg">
            The route does not exist or is no longer available. Use the main
            workspace entry point to continue.
          </p>
        </div>

        <Button asChild size="lg">
          <Link to="/">{t('shell:returnHome')}</Link>
        </Button>
      </div>
    </main>
  )
}

function RootRouteError({ error, reset }: { error: Error; reset: () => void }) {
  const { t } = useTranslation()

  return (
    <main className="flex min-h-[calc(100vh-4rem)] items-center justify-center px-6 py-16">
      <section className="flex max-w-2xl flex-col items-center gap-6 rounded-xl border bg-card px-8 py-10 text-center shadow-sm">
        <div className="flex size-14 items-center justify-center rounded-full bg-destructive/10 text-destructive">
          <AlertTriangle className="size-7" />
        </div>
        <div className="space-y-3">
          <h1 className="text-3xl font-semibold tracking-tight">
            {appTitle} hit a route error
          </h1>
          <p className="text-sm leading-6 text-muted-foreground sm:text-base">
            {error.message || 'An unexpected problem interrupted this route.'}
          </p>
        </div>

        <div className="flex flex-col items-center gap-3 sm:flex-row">
          <Button variant="outline" onClick={reset}>
            {t('shell:tryAgain')}
          </Button>
          <Button asChild>
            <Link to="/">
              <Home className="size-4" />
              {t('shell:returnHome')}
            </Link>
          </Button>
        </div>
      </section>
    </main>
  )
}
